import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { AdminInfo } from "@/types/superAdmin";
import { useTranslation } from "react-i18next";

interface EditAdminDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  admin: AdminInfo | null;
  onSubmit: (data: any) => void;
  isLoading?: boolean;
}

export function EditAdminDialog({ open, onOpenChange, admin, onSubmit, isLoading }: EditAdminDialogProps) {
  const { t } = useTranslation(["superadmin", "common"]);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const isMobile = useIsMobile();

  useEffect(() => {
    if (admin) {
      setFirstName(admin.first_name);
      setLastName(admin.last_name);
      setPhone(admin.phone || "");
    }
  }, [admin]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!admin) return;
    
    onSubmit({
      userId: admin.user_id,
      firstName,
      lastName,
      phone,
    });
  };

  if (!admin) return null;

  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">{t("editAdmin.firstName")}</Label>
          <Input
            id="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lastName">{t("editAdmin.lastName")}</Label>
          <Input
            id="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone">{t("editAdmin.phone")}</Label>
        <Input
          id="phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">{t("editAdmin.emailNonEditable")}</Label>
        <Input
          id="email"
          value={admin.email}
          disabled
          className="bg-muted"
        />
      </div>

      <div className="flex flex-col-reverse md:flex-row justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="w-full md:w-auto">
          {t("common:cancel")}
        </Button>
        <Button type="submit" disabled={isLoading} className="w-full md:w-auto">
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t("common:save")}
        </Button>
      </div>
    </form>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>{t("editAdmin.title")}</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-4">
            {formContent}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("editAdmin.title")}</DialogTitle>
        </DialogHeader>
        {formContent}
      </DialogContent>
    </Dialog>
  );
}
